import React, { useState, useEffect } from 'react';
import { X, Edit2, Save } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const EditInvoiceModal = ({ isOpen, onClose, invoice, onUpdated }) => {
  const [formData, setFormData] = useState({
    number: '',
    internalNumber: '',
    issueDate: '',
    dueDate: '',
    supplierName: '',
    supplierNip: '',
    amountNet: '',
    vatRate: '',
    vatAmount: '',
    amountGross: '',
    currency: 'PLN',
    notes: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (invoice && isOpen) {
      setFormData({
        number: invoice.number || '',
        internalNumber: invoice.internalNumber || '',
        issueDate: invoice.issueDate ? invoice.issueDate.split('T')[0] : '',
        dueDate: invoice.dueDate ? invoice.dueDate.split('T')[0] : '',
        supplierName: invoice.supplierName || '',
        supplierNip: invoice.supplierNip || '',
        amountNet: invoice.amountNet || '',
        vatRate: invoice.vatRate || '',
        vatAmount: invoice.vatAmount || '',
        amountGross: invoice.amountGross || '',
        currency: invoice.currency || 'PLN',
        notes: invoice.notes || ''
      });
    }
  }, [invoice, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updateData = {
        number: formData.number || null,
        internalNumber: formData.internalNumber || null,
        issueDate: formData.issueDate || null,
        dueDate: formData.dueDate || null,
        supplierName: formData.supplierName || null,
        supplierNip: formData.supplierNip || null,
        amountNet: formData.amountNet ? parseFloat(formData.amountNet) : null,
        vatRate: formData.vatRate ? parseFloat(formData.vatRate) : null,
        vatAmount: formData.vatAmount ? parseFloat(formData.vatAmount) : null,
        amountGross: formData.amountGross ? parseFloat(formData.amountGross) : null,
        currency: formData.currency || 'PLN',
        notes: formData.notes || null
      };

      await api.put(`/invoices/${invoice.id}`, updateData);
      toast.success('Faktura została zaktualizowana');
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Błąd podczas aktualizacji faktury:', error);
      const errorMessage = error.response?.data?.error || 'Nie udało się zaktualizować faktury';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Edit2 className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Edytuj fakturę</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-1">
                Numer faktury <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="number"
                name="number"
                value={formData.number}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="internalNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Numer wewnętrzny
              </label>
              <input
                type="text"
                id="internalNumber"
                name="internalNumber"
                value={formData.internalNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="issueDate" className="block text-sm font-medium text-gray-700 mb-1">
                Data wystawienia <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="issueDate"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                Termin płatności
              </label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="supplierName" className="block text-sm font-medium text-gray-700 mb-1">
                Nazwa dostawcy
              </label>
              <input
                type="text"
                id="supplierName"
                name="supplierName"
                value={formData.supplierName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="supplierNip" className="block text-sm font-medium text-gray-700 mb-1">
                NIP dostawcy
              </label>
              <input
                type="text"
                id="supplierNip"
                name="supplierNip"
                value={formData.supplierNip}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="amountNet" className="block text-sm font-medium text-gray-700 mb-1">
                Kwota netto <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="amountNet"
                name="amountNet"
                value={formData.amountNet}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="vatRate" className="block text-sm font-medium text-gray-700 mb-1">
                Stawka VAT (%)
              </label>
              <input
                type="number"
                id="vatRate"
                name="vatRate"
                value={formData.vatRate}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="vatAmount" className="block text-sm font-medium text-gray-700 mb-1">
                Kwota VAT
              </label>
              <input
                type="number"
                id="vatAmount"
                name="vatAmount"
                value={formData.vatAmount}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="amountGross" className="block text-sm font-medium text-gray-700 mb-1">
                Kwota brutto <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="amountGross"
                name="amountGross"
                value={formData.amountGross}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                Waluta
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="UAH">UAH (грн)</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Uwagi
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Zapisywanie...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Zapisz zmiany
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInvoiceModal;
